import { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalizeFirstLetter } from '../../helpers/string';
import { Exam, LATERALITY, TipoExameFormulario } from '../../interfaces/exam';
import { Text } from '../UI-CHAKRA/Text';

import { Flex, FormLabel, Input, Tag, TagLabel, Textarea } from '@chakra-ui/react';
import { format, isValid, parse } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import { get } from 'lodash';
import { ReactComponent as RoundedCloseCidIcon } from '../../assets/images/icons/close/ic-exit-cid-rovered.svg';
import { ReactComponent as CloseCidIcon } from '../../assets/images/icons/close/ic-exit-cid.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/images/icons/mevo/ic-arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/icons/mevo/ic-arrow-up.svg';
import { ReactComponent as StarIcon } from '../../assets/images/icons/mevo/star.svg';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/mevo/trash-outlined.svg';
import { MASKS as CHAKRA_UI_MASKS, MaskedInput } from '../../components/UI-CHAKRA/Input/MaskedInput';
import { Icd } from '../../containers/my-central/patientService';
import { EXAM_INSTRUCTIONS_LIMIT } from '../../containers/prescription/PrescriptionExamContent/PrescriptionExamContent';
import { useIsMobile } from '../../helpers/useIsMobile';
import { usePrescriptionStore } from '../../stores/prescriptionStore';
import { ActionIconButton } from '../ActionIconButton';
import { EXAM_PERIOD_OPTIONS } from '../helpers';
import { FadeSlideMotionBox } from '../Motion/FadeSlideMotionBox';
import { Anchor } from '../notifications/Anchor';
import { ANCHOR_POS, ANCHORS } from '../notifications/notifications';
import SelectExamPeriod from '../SelectExamPeriod';
import { Box } from '../UI-CHAKRA/Box';
import { Button } from '../UI-CHAKRA/Button';
import { Checkbox } from '../UI-CHAKRA/Checkbox';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { Grid } from '../UI-CHAKRA/Grid';
import { GridItem } from '../UI-CHAKRA/GridItem';
import { Radio } from '../UI-CHAKRA/Radio';
import { RadioGroup } from '../UI-CHAKRA/RadioGroup';
import SearchIcd from '../UI-CHAKRA/SearchIcd';
import SelectHighComplexExam from './SelectHighComplexExam';

interface ExamCardProps {
  exam: Exam;
  index: number;
  onDelete(exam: Exam): void;
  onEdit(props: { exam: Exam; index: number }): void;
  onUpdateExam(payload: Exam): Promise<void>;
  onFavoriteExam?(exam: Exam): Promise<void>;
  enableTuss?: boolean;
  hideIndex?: boolean;
  cardIndex?: number;
  shouldCollapse: boolean;
  isFavorite?: boolean;
  registerConfirmCallback?: (cb: () => Promise<void>) => void;
  shouldBeOpen?: boolean;
  needsLaterality?: boolean;
  handleSetLaterality: (value: boolean, lateralityFilled: boolean) => void;
  disabled?: boolean;
  structuredIcd: Icd | null;
  lastGeneralIcd: Icd | null;
}

export default function ExamCard(props: ExamCardProps) {
  const { exam, index, enableTuss, hideIndex, onUpdateExam, cardIndex, shouldCollapse } = props;

  const isAddedByAI = exam.AdicionadoPorIA === true;

  const [laterality, setLaterality] = useState<LATERALITY | null>(null);
  const [useAnesthesia, setUseAnesthesia] = useState<boolean>(false);
  const [showHighComplexityExam, setShowHighComplexityExam] = useState<boolean>(false);
  const [highComplexExam, setHighComplexExam] = useState<TipoExameFormulario | null>(null);
  const [, setIsFocused] = useState(false);
  const [isModified, setIsModified] = useState(false);

  const isMobile = useIsMobile();
  const [quantitySessions, setQuantitySessions] = useState<number | null>(exam.QuantidadeSessoes || 1);
  const [dateString, setDateString] = useState('');

  const [initialValues, setInitialValues] = useState<{
    useAnesthesia: boolean;
    showObservationInput: boolean;
    showHighComplexityExam: boolean;
    laterality: string | null;
    formType: TipoExameFormulario | null;
    observation: string;
    structuredIcd: Icd | null;
  } | null>(null);

  const { prescriberConfiguration } = usePrescriptionStore();

  const showExamForms = get(prescriberConfiguration, 'Features.showExamForms', false);
  const enableExamsAppointmentDateSelection = get(
    prescriberConfiguration,
    'Features.enableExamsAppointmentDateSelection',
    false
  );

  const enableExamsQuantitySelection = get(prescriberConfiguration, 'Features.enableExamsQuantitySelection', false);

  const [observation, setObservation] = useState<string>('');
  const [hasSpecificIcd, setHasSpecificIcd] = useState<boolean>(() => {
    const hasExamStructuredIcd = exam.CID10Estruturado && exam.CID10Estruturado.length > 0;
    return exam.CidEspecifico || hasExamStructuredIcd || false;
  });
  const [icd, setIcd] = useState<Icd | null>(() => {
    if (props.exam.CID10Estruturado && props.exam.CID10Estruturado.length > 0) {
      const cid = props.exam.CID10Estruturado[0];
      return {
        id: cid.idCID10,
        subcategory: cid.Codigo,
        description: cid.Descricao,
      };
    }
    return props.structuredIcd || null;
  });
  const [isEditingIcd, setIsEditingIcd] = useState(false);

  const [showObservationInput, setShowObservationInput] = useState(false);

  const [isOpen, setIsOpen] = useState(false);

  const [isCidChipHovered, setIsCidChipHovered] = useState(false);
  const [isCidInputFocused, setIsCidInputFocused] = useState(false);
  const [isCloseIconHovered, setIsCloseIconHovered] = useState(false);

  const onEdit = () => {
    props.onEdit({ exam, index });
  };

  const onChangeLaterality = (value: LATERALITY) => {
    setLaterality(value);
    props.handleSetLaterality(false, true);
  };

  const onChangeUseAnesthesia = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUseAnesthesia(event.target.checked);
  };

  const handleClearIcd = () => {
    setHasSpecificIcd(false);
    setIcd(null);
    setIsEditingIcd(true);
    setIsCidInputFocused(true);
    onUpdateExam({
      ...exam,
      CID10Estruturado: props.structuredIcd ? [{
        idCID10: props.structuredIcd.id,
        Codigo: props.structuredIcd.subcategory,
        Descricao: props.structuredIcd.description,
      }] : [],
      CidEspecifico: false,
    });
  };

  const showCidSearchInput = (isCidChipHovered || isCidInputFocused) && icd === null;

  const onChangeShowObservationInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;

    if (!value) {
      setObservation('');
    }

    setShowObservationInput(value);
  };

  const onBlurObservation = () => {
    setIsFocused(false);
    const hasObservation = observation && observation.trim().length > 0;

    onUpdateExam({
      ...exam,
      Instrucoes: observation,
      ObservacaoEspecifica: hasObservation ? true : false,
      Lateralidade: laterality,
      AplicarAnestesia: useAnesthesia,
      tipoFormulario: showHighComplexityExam ? highComplexExam : null,
      QuantidadeSessoes: quantitySessions ?? 1,
    });
  };

  const confirmEdition = useCallback(async () => {
    const payload: Exam = {
      ...(exam as Exam),
      AplicarAnestesia: useAnesthesia,
      Instrucoes: observation,
      Lateralidade: laterality,
      CID10Estruturado: [],
      tipoFormulario: showHighComplexityExam ? highComplexExam : null,
      QuandoRealizar: exam.QuandoRealizar || 'Rotina',
      DataEspecifica: exam.DataEspecifica,
      QuantidadeSessoes: quantitySessions ?? 1,
    };

    if (icd !== null && Array.isArray(payload.CID10Estruturado)) {
      const icdStructured = { idCID10: icd.id, Codigo: icd.subcategory, Descricao: icd.description };
      payload.CID10Estruturado.push(icdStructured);
    }

    await onUpdateExam(payload);
    setIsOpen(false);
  }, [
    exam,
    highComplexExam,
    icd,
    laterality,
    observation,
    onUpdateExam,
    showHighComplexityExam,
    useAnesthesia,
    quantitySessions,
  ]);

  const onChangeObservation = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setObservation(event.target.value);
  };

  const highComplexExamList = useMemo((): TipoExameFormulario[] => {
    return exam.tipoExameFormulario
      ? exam.tipoExameFormulario.map((tipo) => ({
        Nome: tipo.Nome,
        Chave: tipo.Chave,
        idTipoExameFormulario: tipo.idTipoExameFormulario,
      }))
      : [];
  }, [exam.tipoExameFormulario]);

  const handleToggle = () => {
    if (!shouldCollapse) {
      onEdit();
      return;
    }
    setIsOpen(!isOpen);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStringValue = event.target.value;

    setDateString(newStringValue);

    if (!newStringValue || newStringValue.includes('_')) {
      onUpdateExam({ ...exam, DataEspecifica: null });
      return;
    }

    const parsedDate = parse(newStringValue, 'dd/MM/yyyy', new Date());

    if (isValid(parsedDate)) {
      onUpdateExam({ ...exam, DataEspecifica: parsedDate });
    } else {
      onUpdateExam({ ...exam, DataEspecifica: null });
    }
  };

  useEffect(() => {
    const initialValue = exam.DataEspecifica ? format(exam.DataEspecifica, 'dd/MM/yyyy') : '';
    setDateString(initialValue);
  }, [exam.DataEspecifica]);

  useEffect(() => {
    const initialValues = {
      useAnesthesia: exam.AplicarAnestesia || false,
      showObservationInput: !!exam.Instrucoes,
      showHighComplexityExam: Boolean(exam.tipoFormulario && exam.tipoFormulario.Chave !== null),
      laterality: exam.Lateralidade || null,
      formType: exam.tipoFormulario || null,
      observation: exam.Instrucoes || '',
      structuredIcd: props.structuredIcd
        ? {
          description: props.structuredIcd.description,
          id: props.structuredIcd.id,
          subcategory: props.structuredIcd.subcategory,
        }
        : null,
    };

    setLaterality(initialValues.laterality);

    setShowHighComplexityExam(initialValues.showHighComplexityExam);

    if (showExamForms) {
      setHighComplexExam(initialValues.formType);
    }

    setUseAnesthesia(initialValues.useAnesthesia);

    setObservation(initialValues.observation);
    setShowObservationInput(initialValues.showObservationInput);

    setInitialValues(initialValues);
  }, [exam, showExamForms, props.structuredIcd]);

  useEffect(() => {
    if (!initialValues) return;

    const hasChanges =
      laterality !== initialValues.laterality ||
      useAnesthesia !== initialValues.useAnesthesia ||
      showObservationInput !== initialValues.showObservationInput ||
      showHighComplexityExam !== initialValues.showHighComplexityExam ||
      highComplexExam?.Chave !== initialValues.formType?.Chave ||
      observation !== initialValues.observation ||
      icd?.id !== initialValues.structuredIcd?.id ||
      icd?.subcategory !== initialValues.structuredIcd?.subcategory;

    setIsModified(hasChanges);
  }, [
    initialValues,
    laterality,
    showHighComplexityExam,
    showObservationInput,
    useAnesthesia,
    observation,
    highComplexExam?.Chave,
    icd?.id,
    icd?.subcategory,
  ]);

  useEffect(() => {
    if (shouldCollapse) {
      setIsOpen(true);
    }
  }, [shouldCollapse]);

  useEffect(() => {
    if (props.registerConfirmCallback) {
      props.registerConfirmCallback(confirmEdition);
    }
  }, [confirmEdition, props]);

  useEffect(() => {
    if (exam.LadoRequerido && shouldCollapse && !exam.Lateralidade) {
      setIsOpen(true);
    }
  }, [exam, shouldCollapse]);

  useEffect(() => {
    const actualIcd = icd
    const hasExamStructuredIcd = exam.CID10Estruturado && exam.CID10Estruturado.length > 0;

    if (actualIcd?.id !== props.lastGeneralIcd?.id && actualIcd?.description !== props.lastGeneralIcd?.description) return

    setHasSpecificIcd(exam.CidEspecifico || hasExamStructuredIcd || false);

    if (!hasExamStructuredIcd && !isEditingIcd && !exam.CidEspecifico) {
      setIcd(props.structuredIcd);
    }
  }, [props.structuredIcd, isEditingIcd, exam.CID10Estruturado, exam.CidEspecifico, icd, props.lastGeneralIcd])

  return (
    <Box
      id={`included-exam-${cardIndex}`}
      padding={'14px 16px'}
      borderRadius={'10px'}
      bg={'#fff'}
      boxShadow={'3px 3px 10px rgba(52, 58, 64, 0.10)'}
      position="relative"
    >
      <Box
        onClick={handleToggle}
        p={0}
        m={0}
        w={'100%'}
        h={'100%'}
        cursor={'pointer'}
        role={'button'}
        tabIndex={0}
        position="relative"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <Grid templateColumns={'repeat(24, 1fr)'} w={'100%'}>
          {!hideIndex && (
            <GridItem display={'flex'} justifyContent={'center'} alignItems={'center'} colSpan={{ base: 2, md: 1 }}>
              <Text fontWeight={700} color={'#868E96'} textAlign={'center'} fontSize={'14px'}>
                {props.index}
              </Text>
            </GridItem>
          )}
          <GridItem colSpan={{ base: 22, md: hideIndex ? 23 : 22 }} display={'flex'} alignItems={'center'}>
            <Flex
              alignItems={'center'}
              h={'100%'}
              justifyContent={'flex-start'}
              mt={{ base: '10px', md: 0 }}
              ml="-14px"
              position="relative"
            >
              {props.onFavoriteExam && exam && exam.referencia && exam.examId && exam.examId !== 'undefined' && (
                <Button
                  variant={'ghost'}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (props.onFavoriteExam) {
                      props.onFavoriteExam(exam);
                    }
                  }}
                  p={0}
                  m={0}
                  gap={'10px'}
                  data-testid={'favorite-exam'}
                  position="relative"
                >
                  <Anchor
                    anchorName={ANCHORS.EXAM_FAVORITE}
                    position={isMobile ? ANCHOR_POS.TOP_MOBILE : ANCHOR_POS.LEFT}
                    fitWidth
                  >
                    <StarIcon
                      stroke={props.isFavorite ? '#FF9292' : '#AA95D0'}
                      fill={props.isFavorite ? '#FFDE49' : '#fff'}
                      width={'16px'}
                      height={'16px'}
                    />
                  </Anchor>
                </Button>
              )}
              <Flex flexDirection={'column'} alignSelf={enableTuss ? 'flex-end' : 'center'}>
                {isAddedByAI && (
                  <Flex mb="8px">
                    <Tag
                      size="md"
                      bg="#E3F2FD"
                      color="#1976D2"
                      borderRadius="16px"
                      fontWeight="600"
                      fontSize="12px"
                      px="12px"
                      py="4px"
                      height="auto"
                    >
                      <TagLabel>Adicionado por áudio</TagLabel>
                    </Tag>
                  </Flex>
                )}
                <Text fontWeight={600} fontSize={'14px'} wordBreak="break-word" whiteSpace="normal" textAlign={'left'}>
                  {capitalizeFirstLetter(exam.Nome)}
                </Text>
                {enableTuss && (
                  <Text data-testid={'tuss'} fontSize={'10px'} color={'#868E96'} fontWeight={600} textAlign={'left'}>
                    {`TUSS: ${exam.CodigoTUSS}`}
                  </Text>
                )}
              </Flex>
            </Flex>
          </GridItem>
          <GridItem colSpan={{ base: 24, md: 1 }} mb={{ base: '10px', md: 0 }}>
            <Flex alignItems={'center'} h={'100%'} justifyContent={'flex-end'} mt={{ base: '10px', md: 0 }} gap={'8px'}>
              {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
              {!props.disabled && (
                <ActionIconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onDelete(exam);
                  }}
                  iconColor={'gray.main'}
                  iconHoverColor={'error.main'}
                  Icon={<TrashIcon />}
                  role={'delete'}
                  dataCy={'removeExam'}
                />
              )}
            </Flex>
          </GridItem>
        </Grid>
      </Box>
      <Box
        onMouseEnter={() => setIsCidChipHovered(true)}
        onMouseLeave={() => setIsCidChipHovered(false)}
        minW={showCidSearchInput ? '280px' : 'auto'}
        transition="min-width 0.2s ease"
        position="relative"
        mt="8px"
      >
        {showCidSearchInput ? (
          <SearchIcd
            icd={icd}
            setIcd={(selectedIcd) => {
              setIcd(selectedIcd);
              setIsEditingIcd(false);
              if (selectedIcd) {
                setIsCidChipHovered(false);
                setIsCidInputFocused(false);
                setHasSpecificIcd(true);
                const icdStructured = {
                  idCID10: selectedIcd.id,
                  Codigo: selectedIcd.subcategory,
                  Descricao: selectedIcd.description,
                };
                onUpdateExam({
                  ...exam,
                  CID10Estruturado: [icdStructured],
                  CidEspecifico: true,
                });
              }
            }}
            menuMaxHeight={'170px'}
            placeholder={'Buscar CID'}
            onFocus={() => setIsCidInputFocused(true)}
            onBlur={() => {
              setTimeout(() => {
                setIsCidInputFocused(false);
                setIsEditingIcd(false);
              }, 200);
            }}
          />
        ) : (
          <Tag
            size="md"
            bg={icd ? '#FFF4C0' : '#fff'}
            color={icd ? '#343A40' : 'primary.main'}
            borderRadius="20px"
            fontWeight={600}
            fontSize="13px"
            px="14px"
            py="6px"
            height="auto"
            cursor="pointer"
            transition="all 0.2s ease"
            _hover={{
              bg: icd ? 'rgba(255, 244, 192, 0.5)' : '#343A40',
            }}
          >
            <TagLabel>
              {icd ? `${icd.subcategory} - ${icd.description}` : 'Adicionar indicação clínica específica'}
            </TagLabel>
            {icd && (
              <Box
                onClick={handleClearIcd}
                ml="6px"
                cursor="pointer"
                onMouseEnter={() => setIsCloseIconHovered(true)}
                onMouseLeave={() => setIsCloseIconHovered(false)}
                display="flex"
                alignItems="center"
              >
                {isCloseIconHovered ? (
                  <RoundedCloseCidIcon width="16px" height="16px" />
                ) : (
                  <CloseCidIcon width="16px" height="16px" />
                )}
              </Box>
            )}
          </Tag>
        )}
      </Box>
      <AnimatePresence>
        {isOpen && (
          <Box mt={'5px'} position="relative">
            <FadeSlideMotionBox>
              {exam.LadoRequerido && (
                <div>
                  <Box>
                    <RadioGroup
                      w={'100%'}
                      onChange={onChangeLaterality}
                      value={String(laterality)}
                      mb={props.needsLaterality ? '' : '16px'}
                      size={'sm'}
                    >
                      <Flex flexWrap={'wrap'} gap={'10px'} justifyContent={'flex-start'}>
                        <Radio
                          style={!exam.Lateralidade && props.needsLaterality ? { border: '2px solid #E53E3E' } : {}}
                          value={LATERALITY.LEFT}
                        >
                          Esquerdo
                        </Radio>
                        <Radio
                          style={!exam.Lateralidade && props.needsLaterality ? { border: '2px solid #E53E3E' } : {}}
                          value={LATERALITY.RIGHT}
                        >
                          Direito
                        </Radio>
                        <Radio
                          style={!exam.Lateralidade && props.needsLaterality ? { border: '2px solid #E53E3E' } : {}}
                          value={LATERALITY.BOTH}
                        >
                          Ambos
                        </Radio>
                      </Flex>
                    </RadioGroup>
                    {!exam.Lateralidade && props.needsLaterality && (
                      <Text fontSize={12} fontWeight={700} color="#E53E3E">
                        Selecione a lateralidade
                      </Text>
                    )}
                  </Box>
                </div>
              )}

              {(enableExamsAppointmentDateSelection || enableExamsQuantitySelection) && (
                <Grid
                  gridTemplateColumns={exam.QuandoRealizar === EXAM_PERIOD_OPTIONS[3] ? '1fr 1fr' : '1fr'}
                  gap={'16px'}
                  mb={'16px'}
                >
                  {enableExamsAppointmentDateSelection && (
                    <>
                      <SelectExamPeriod
                        value={exam.QuandoRealizar || 'Rotina'}
                        onChange={(value) => {
                          onUpdateExam({ ...exam, QuandoRealizar: value });
                        }}
                      />
                      {exam.QuandoRealizar === EXAM_PERIOD_OPTIONS[3] && (
                        <FormControl>
                          <FormLabel color={'#868E96'} fontWeight={700} whiteSpace={'nowrap'}>
                            Data Específica
                          </FormLabel>
                          <MaskedInput
                            w={'100%'}
                            value={dateString}
                            onChange={handleDateChange}
                            placeholder={'__/__/____'}
                            mask={CHAKRA_UI_MASKS.BIRTH_DATE}
                            isInvalid={
                              dateString.length !== 10 ||
                              !isValid(parse(dateString, 'dd/MM/yyyy', new Date())) ||
                              new Date(dateString) < new Date()
                            }
                          />
                        </FormControl>
                      )}
                    </>
                  )}
                </Grid>
              )}

              <Flex flexDirection={'column'} mb={'16px'} gap={'16px'} flexWrap={'wrap'} alignItems={'left'}>
                {exam.AnestesiaRequerida && (
                  <Checkbox isChecked={useAnesthesia} onChange={onChangeUseAnesthesia}>
                    Uso de anestesia
                  </Checkbox>
                )}

                {highComplexExamList.length > 0 && showExamForms && (
                  <Flex gap="16px">
                    {highComplexExamList.map((tipo) => (
                      <Checkbox
                        key={tipo.idTipoExameFormulario}
                        isChecked={highComplexExam?.Chave === tipo.Chave}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setHighComplexExam(tipo);
                            setShowHighComplexityExam(true);
                          } else {
                            setHighComplexExam(null);
                            setShowHighComplexityExam(false);
                          }
                        }}
                      >
                        {tipo.Chave === 'FORMULARIO_APAC' ? 'APAC' : 'BPA-I'}
                      </Checkbox>
                    ))}
                  </Flex>
                )}

                <Flex alignItems="center" gap="12px" justifyContent="space-between">
                  <Text fontSize="12px" fontWeight={600} color="#868E96">
                    Quantidade solicitada:
                  </Text>
                  <Flex alignItems="center" border="1px solid #E0E0E0" borderRadius="8px" px="8px" py="2px" bg="white">
                    <Box
                      as="button"
                      onClick={() => setQuantitySessions(Math.max(1, (quantitySessions ?? 1) - 1))}
                      cursor={quantitySessions === 1 ? 'default' : 'pointer'}
                      px="6px"
                      py="2px"
                      fontSize="16px"
                      fontWeight={600}
                      color={quantitySessions === 1 ? '#868E96' : 'primary.main'}
                      opacity={quantitySessions === 1 ? 0.3 : 1}
                      _hover={{ color: '#343A40' }}
                      transition="color 0.2s"
                      bg="transparent"
                      border="none"
                    >
                      −
                    </Box>
                    <Input
                      type="number"
                      value={quantitySessions ?? 1}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        setQuantitySessions(Math.max(1, val));
                      }}
                      min={1}
                      textAlign="center"
                      w="36px"
                      h="24px"
                      px="0"
                      fontSize="14px"
                      fontWeight={600}
                      border="none"
                      bg="transparent"
                      _focus={{ boxShadow: 'none' }}
                      sx={{
                        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        MozAppearance: 'textfield',
                      }}
                    />
                    <Box
                      as="button"
                      onClick={() => setQuantitySessions((quantitySessions ?? 1) + 1)}
                      cursor="pointer"
                      px="6px"
                      py="2px"
                      fontSize="16px"
                      fontWeight={600}
                      color="primary.main"
                      _hover={{ color: '#343A40' }}
                      transition="color 0.2s"
                      bg="transparent"
                      border="none"
                    >
                      +
                    </Box>
                  </Flex>
                </Flex>

                <Checkbox
                  isChecked={showObservationInput}
                  onChange={onChangeShowObservationInput}
                  defaultChecked={showObservationInput}
                >
                  Observação/justificativa específica
                </Checkbox>
              </Flex>

              {showObservationInput && (
                <Flex flexDir={'column'} gap={'8px'}>
                  <Textarea
                    resize={'none'}
                    value={observation}
                    onChange={onChangeObservation}
                    placeholder="Adicionar indicação clínica para o exame (opcional)"
                    maxLength={EXAM_INSTRUCTIONS_LIMIT}
                    rows={3}
                    onFocus={() => setIsFocused(true)}
                    onBlur={onBlurObservation}
                    transition={'0.3s all'}
                    height={'56px'}
                    _focus={{
                      height: '80px',
                      border: 'none',
                    }}
                  />
                </Flex>
              )
              }
            </FadeSlideMotionBox >
          </Box >
        )}
      </AnimatePresence>
    </Box>
  );
}
